import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { api, ApiError, staleCopy } from "../api";
import { Markdown, WikilinkContext } from "../markdown";
import type { NoteCollection, NoteEntry, NoteIndexEntry } from "../types";
import { ChevronLeftIcon, ChevronRightIcon, MenuIcon, PencilIcon, SearchIcon } from "../icons";
import { tintOf } from "../avatar";
import { useIsNarrow } from "../viewport";
import { usePhoneDetail } from "./phonePage";
import { StaleNotice } from "../components/StaleNotice";
import { groupNotes } from "./notesGrouping.ts";

/**
 * Reading the notes. The shelf on the left, one note open on the right; on a phone the note is a page over the shelf.
 *
 * Deliberately not a file tree: the notebooks are addressed by kind and tag as filters over one
 * list, because the way in is usually "everything I explained myself" or "this project's", not a
 * path. A kind may say how it is grouped and ordered, and whether its notes may be edited here; every
 * other note is corrected by telling the agent (ADR-0042).
 */
export function Notes({ onMenu }: { onMenu: () => void }) {
	const [collections, setCollections] = useState<NoteCollection[] | null>(null);
	const [current, setCurrent] = useState<string | null>(null);
	const [entries, setEntries] = useState<NoteIndexEntry[]>([]);
	const [tags, setTags] = useState<string[]>([]);
	const [kind, setKind] = useState<string | null>(null);
	const [tag, setTag] = useState<string | null>(null);
	const [query, setQuery] = useState("");
	const [open, setOpen] = useState<NoteEntry | null>(null);
	const [notice, setNotice] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(true);
	/** The body being edited, or null while the note is only being read. */
	const [draft, setDraft] = useState<string | null>(null);
	/** A save refused because the note moved on: what is there now. */
	const [stale, setStale] = useState<NoteEntry | null>(null);
	const [saving, setSaving] = useState(false);
	/** Bumped after a save, so the list shows the new date. */
	const [listRevision, setListRevision] = useState(0);

	const collection = useMemo(() => collections?.find((c) => c.id === current) ?? null, [collections, current]);
	const phone = usePhoneDetail(useIsNarrow(), "notePage", () => {
		setOpen(null);
		setDraft(null);
		setStale(null);
	});

	useEffect(() => {
		api
			.notebooks()
			.then(({ collections: found }) => {
				setCollections(found);
				setCurrent((id) => id ?? found[0]?.id ?? null);
			})
			.catch((err) => setError(err instanceof ApiError ? err.message : "加载笔记失败"))
			.finally(() => setLoading(false));
	}, []);

	// A filter that survived a notebook switch would silently hide everything in the new one.
	useEffect(() => {
		setKind(null);
		setTag(null);
		setQuery("");
		setOpen(null);
	}, [current]);

	useEffect(() => {
		if (!current) return;
		let live = true;
		const timer = setTimeout(() => {
			api
				.notes(current, { kind: kind ?? undefined, tag: tag ?? undefined, q: query.trim() || undefined })
				.then((res) => {
					if (!live) return;
					setEntries(res.entries);
					setTags(res.tags);
				})
				.catch((err) => live && setError(err instanceof ApiError ? err.message : "加载笔记失败"));
		}, query ? 200 : 0);
		return () => {
			live = false;
			clearTimeout(timer);
		};
	}, [current, kind, tag, query, listRevision]);

	/** Resolves true once the note is open. */
	const read = useCallback(
		async (path: string) => {
			if (!current) return false;
			setNotice(null);
			setDraft(null);
			setStale(null);
			try {
				const { entry } = await api.note(current, path);
				setOpen(entry);
				return true;
			} catch (err) {
				setError(err instanceof ApiError ? err.message : "打开笔记失败");
				return false;
			}
		},
		[current],
	);

	/** A note picked from the list: on a phone it opens as a page over the list. */
	async function pick(path: string) {
		if (await read(path)) phone.opened();
	}

	/** A `[[basename]]` in the note being read. Nothing resolves outside the open notebook. */
	const follow = useCallback(
		(name: string) => {
			if (!current) return;
			void api
				.noteLink(current, name)
				.then(({ entry }) => {
					if (entry) void read(entry.path);
					else setNotice(`未找到「${name}」`);
				})
				.catch(() => setNotice(`未找到「${name}」`));
		},
		[current, read],
	);

	const labelFor = (name: string) => collection?.kinds.find((k) => k.name === name)?.label ?? name;
	const editable = Boolean(open && collection?.kinds.find((k) => k.name === open.kind)?.editable);
	// Grouped only when nothing is searched for: a search result is ranked, not laid out.
	const laidOut = useMemo(() => (query.trim() || !collection ? { groups: [], rest: entries } : groupNotes(entries, collection.kinds)), [entries, collection, query]);

	/** Save over the version the reader opened, or over `ifVersion` once the person chose to overwrite. */
	async function save(ifVersion = open?.version) {
		if (!current || !open || draft === null || !ifVersion) return;
		setSaving(true);
		try {
			const { entry } = await api.noteSave(current, open.path, draft, ifVersion);
			setOpen(entry);
			setDraft(null);
			setStale(null);
			setListRevision((v) => v + 1);
		} catch (err) {
			const copy = staleCopy<NoteEntry>(err);
			if (copy) setStale(copy.current);
			else setError(err instanceof ApiError ? err.message : "保存失败");
		} finally {
			setSaving(false);
		}
	}

	const row = (e: NoteIndexEntry) => (
		<button key={e.path} className={`list-row${open?.path === e.path ? " current" : ""}`} onClick={() => void pick(e.path)}>
			<span className="list-row-head">
				<span className="list-row-title">{e.title}</span>
				<span className={`pill tint-${tintOf(e.kind)}`}>{labelFor(e.kind)}</span>
			</span>
			{e.description && <span className="list-row-sub">{e.description}</span>}
			<span className="list-row-meta">{[e.tags.map((t) => `#${t}`).join(" "), e.updated].filter(Boolean).join(" · ")}</span>
		</button>
	);

	return (
		<main className={`chat notes split-page${open ? " detail-open" : ""}`}>
			{error && (
				<p className="banner error">
					{error}
					<button className="banner-close" title="关闭" onClick={() => setError(null)}>
						×
					</button>
				</p>
			)}
			<div className="split">
				<section className="split-list">
					<header className="chat-head page-head">
						<button className="ghost sidebar-toggle icon-btn" aria-label="菜单" onClick={onMenu}>
							<MenuIcon size={22} />
						</button>
						<div className="titles">
							<span className="title">笔记</span>
						</div>
					</header>
					<div className="split-scroll">
						{loading ? (
							<p className="hint list-hint">加载中…</p>
						) : !collections || collections.length === 0 ? (
							<p className="hint list-hint">暂无笔记</p>
						) : (
							<>
								<div className="list-tools">
									<label className="search-field">
										<SearchIcon size={17} />
										<input type="search" aria-label="搜索笔记" value={query} placeholder="搜索笔记" onChange={(e) => setQuery(e.target.value)} />
									</label>
									{collections.length > 1 && (
										<div className="chip-row" role="group" aria-label="笔记本">
											{collections.map((c) => (
												<button key={c.id} className={`chip${c.id === current ? " on" : ""}`} aria-pressed={c.id === current} onClick={() => setCurrent(c.id)}>
													{c.label} <span className="chip-count">{c.count}</span>
												</button>
											))}
										</div>
									)}
									{collection && collection.kinds.length > 1 && (
										<div className="chip-row small" role="group" aria-label="类型">
											<button className={`chip${kind === null ? " on" : ""}`} aria-pressed={kind === null} onClick={() => setKind(null)}>
												全部
											</button>
											{collection.kinds.map((k) => (
												<button key={k.name} className={`chip${kind === k.name ? " on" : ""}`} aria-pressed={kind === k.name} onClick={() => setKind(k.name)}>
													{k.label}
												</button>
											))}
										</div>
									)}
									{tags.length > 0 && (
										<div className="chip-row small" role="group" aria-label="标签">
											{tags.map((t) => (
												<button key={t} className={`chip tag${tag === t ? " on" : ""}`} aria-pressed={tag === t} onClick={() => setTag(tag === t ? null : t)}>
													{t}
												</button>
											))}
										</div>
									)}
								</div>
								{entries.length === 0 ? (
									<p className="hint list-hint">{query || kind || tag ? "无匹配笔记" : "暂无笔记"}</p>
								) : (
									<>
										{laidOut.groups.map((g) => (
											<Fragment key={`${g.entries[0]!.kind}:${g.key}`}>
												<h3 className="list-label">{g.label}</h3>
												<div className="row-group">{g.entries.map(row)}</div>
											</Fragment>
										))}
										{laidOut.rest.length > 0 && <div className="row-group">{laidOut.rest.map(row)}</div>}
									</>
								)}
							</>
						)}
					</div>
				</section>
				<section className="split-detail">
					{open && (
						<>
							<header className="detail-head">
								<button className="ghost icon-btn detail-back" aria-label="返回" title="返回" onClick={phone.back}>
									<ChevronLeftIcon size={24} />
								</button>
								<span className="detail-crumb">
									{collection && <span className="detail-crumb-dim">{collection.label}</span>}
									{collection && <ChevronRightIcon size={14} />}
									<span>{labelFor(open.kind)}</span>
								</span>
								{editable && draft === null && (
									<button className="head-btn" onClick={() => setDraft(open.body)}>
										<PencilIcon size={15} />
										编辑
									</button>
								)}
							</header>
							<div className="detail-body">
								<article className="note-view">
									<h1>{open.title}</h1>
									{open.description && <p className="note-description">{open.description}</p>}
									<p className="note-meta">
										<span className={`pill tint-${tintOf(open.kind)}`}>{labelFor(open.kind)}</span>
										{[open.updated && `更新于 ${open.updated}`, open.tags.map((t) => `#${t}`).join(" ")].filter(Boolean).join(" · ")}
									</p>
									{notice && <p className="hint">{notice}</p>}
									{stale !== null && draft !== null && (
										<StaleNotice
											onReload={() => {
												setOpen(stale);
												setDraft(null);
												setStale(null);
											}}
											onOverwrite={() => void save(stale.version)}
										/>
									)}
									{draft === null ? (
										<WikilinkContext.Provider value={follow}>
											<Markdown text={open.body} />
										</WikilinkContext.Provider>
									) : (
										<div className="note-editor">
											<textarea aria-label="正文" value={draft} onChange={(e) => setDraft(e.target.value)} rows={20} />
											<div className="row">
												<button className="primary" disabled={saving || !draft.trim()} onClick={() => void save()}>
													{saving ? "保存中…" : "保存"}
												</button>
												<button
													className="ghost"
													onClick={() => {
														setDraft(null);
														setStale(null);
													}}
												>
													取消
												</button>
											</div>
										</div>
									)}
								</article>
							</div>
						</>
					)}
				</section>
			</div>
		</main>
	);
}
